import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RuleLevel } from './models/rule-level';
import { Product } from '../../../models/product';
import { SortBase } from './models/sort-base';
import { BoxElement } from '../../../models/elements/boxElement';
import { Block } from '../../../models/block';
import { Utils } from '../../../../core/services/utils.service';
import { EquipmentBase } from '../../../models/equipment-base';
import { ProductGroup } from '../../../models/product-group';
import { Element } from '../../../../equipment/models/elements/element';
import { EquipmentElements } from '../../../../equipment/models/elements/equipmentElements';
import { ShelfElement } from '../../../models/elements/shelfElement';
import { ExpandFaces } from './models/expandFaces';
import { ExpandParams } from './models/expandParams';
import { ExpandFactory } from './models/expandStrategy';
import { Bounding } from '../../../../core/models/bounding';
import { GoldenShelf } from '../../../../core/models/goldenShelf';
import { ProductsService } from '../../../../core/services/products.service';
import { IPlanogram } from '../../../models/planogram';
import { ReportParams } from '../../../../core/models/reportParams';
import { DateOptions, LayoutBlock } from './layout-rules.component';
import { Names } from './models/group-base';
import { SessionService } from '../../../../core/services/session.service';
import { Observable } from 'rxjs';
import { ProductRotationPointType } from '../../../../core/models/personalSettings';

const PERCENT_ACCURACY: number = 2;

type LayoutGroup = {
	level: RuleLevel;
	children: Array<LayoutGroup>;
	products: Array<Product>;
	sort: Array<SortBase>;
	width?: number;
	limits?: {
		from: number;
		to: number;
	},
	shelfLimits?: Array<Limit>;
	boxes?: Array<BoxElement>;
	blocks?: Array<Block>;
	settings?: GroupSettings;
}

type GroupSettings = {
	brand: string;
	supplier: string;
	category: string;
	characteristic: string;
}

type GroupParams = {
	level: RuleLevel;
	products: Product[];
	planogram: EquipmentBase;
	activeBlock: LayoutBlock;
	percent: number;
	settings: GroupSettings;
	sort: SortBase[];
}

type Limit = {
	altitude: number;
	min: number;
	strictMin: boolean;
	max: number;
	strictMax: boolean;
	shelves: Array<Element>;
	blocks: Array<Block>;
	level?: RuleLevel;
	sort?: Array<SortBase>;
	get width(): number;
}

type BlockLimit = Limit & {
	level: RuleLevel;
	sort: Array<SortBase>;
}

type InsertParams = {
	index: number;
	from: number;
	limits: Array<Limit | BlockLimit>;
	level: RuleLevel;
	planogram: EquipmentBase;
	insertedProducts: Array<ProductGroup>;
	extraSpace: number;
}

type DivideParams = {
	groups: Array<LayoutGroup>;
	products: Array<Product>;
	planogram: EquipmentBase;
	activeBlock: LayoutBlock;
	from: number;
	to: number;
}

type FacesProduct = {
	qty: number;
	product: Product;
}

type ApplyRuleParams = {
	planogram: IPlanogram;
	activeBlock: LayoutBlock;
	ruleLevels: RuleLevel;
	products: Array<Product>;
	reportParams: ReportParams;
	selectedStores: Array<number>;
	dateOptions: DateOptions;
	useSales: boolean;
	expandFaces: ExpandFaces;
	checkDistance: boolean;
	names: Names;
}

class RuleExecution {
	private readonly _blocksToLevel: Map<RuleLevel, { limits: Array<BlockLimit>, products: Array<Product> }>;
	private _blocksInsertedProducts: Array<Product>;
	private readonly _levelToProduct: Map<ProductGroup, RuleLevel>;
	private readonly _percentToProduct: Map<Product, number>;
	private readonly _productsToLevel: Map<RuleLevel, Array<ProductGroup>>;
	private _translate: any;
	private _limitMatrix: any = {};
	public checkDistance: boolean = true;
	public expandFaces: ExpandFaces = ExpandFaces.Default;
	public names: Names;
	public productRotationPoint: ProductRotationPointType = 'center';
	public useSales: boolean = true;
	public reportParams: any;
	public placePredict: any;
	public percentDictionary: any;

	constructor(translateCallback: (key: string, params: any) => Observable<any>) {
		this._blocksToLevel = new Map<RuleLevel, { limits: Array<BlockLimit>, products: Array<Product> }>();
		this._blocksInsertedProducts = [];
		this._levelToProduct = new Map<ProductGroup, RuleLevel>();
		this._percentToProduct = new Map<Product, number>();
		this._productsToLevel = new Map<RuleLevel, Array<ProductGroup>>();
		this._translate = translateCallback;
		this.reportParams = {};
		this.placePredict = {};
		this.percentDictionary = {};
	}

	public setCheckDistance(checkDistance: boolean): RuleExecution {
		this.checkDistance = checkDistance ?? true;
		return this;
	}

	public setExpandFaces(expandFaces: ExpandFaces): RuleExecution {
		this.expandFaces = expandFaces ?? ExpandFaces.Default;
		return this;
	}

	public setNames(names: Names): RuleExecution {
		this.names = names;
		return this;
	}

	public setProductRotationPoint(point: ProductRotationPointType): RuleExecution {
		this.productRotationPoint = point ?? 'center';
		return this;
	}

	public setUseSales(useSales: boolean): RuleExecution {
		this.useSales = useSales ?? true;
		return this;
	}

	private addBlockLimit(block: Block, limit: Limit, group: LayoutGroup, products: Array<Product>): void {
		const min: number = Math.max(block.boundingX.min, limit.min),
			max: number = Math.min(block.boundingX.max, limit.max),
			blockLimit: BlockLimit = {
				altitude: 0,
				min: min,
				strictMin: true,
				max: max,
				strictMax: true,
				shelves: limit.shelves.filter((shelf: Element) => shelf.shapes[0].inRange({ from: min, to: max })),
				blocks: limit.blocks.filter((block: Block) => block.inRange({ from: min, to: max })),
				level: group.level,
				sort: group.sort,
				get width() {
					return Utils.round(this.max - this.min);
				}
			};

		const level: RuleLevel = group.level.parent && group.level.parent.group.isHorizontal
			? group.level.parent
			: group.level;

		if (!this._blocksToLevel.has(level)) {
			this._blocksToLevel.set(level, { limits: [blockLimit], products: [...products] });
		} else {
			this._blocksToLevel.get(level).limits.push(blockLimit);
		}
	}

	// insert product to planogram
	private addProductToPlanogram(
		planogram: EquipmentBase,
		insertedProducts: Array<ProductGroup>,
		productGroup: ProductGroup,
		level: RuleLevel
	): void {
		const totalWidth: number = Utils.round(planogram.getShelvesLimits().reduce((a, b) => a + b.width, 0)),
			percent: number = productGroup.Width / totalWidth * 100;

		planogram.products.push(productGroup);
		insertedProducts.push(productGroup);

		if (this._productsToLevel.has(level)) {
			this._productsToLevel.get(level).push(productGroup);
		} else {
			this._productsToLevel.set(level, [productGroup]);
		}

		this._levelToProduct.set(productGroup, level);

		if (this._percentToProduct.has(productGroup.product)) {
			this._percentToProduct.set(productGroup.product, this._percentToProduct.get(productGroup.product) + percent);
		} else {
			this._percentToProduct.set(productGroup.product, percent);
		}
	}

	public calculateActualPercent(group: LayoutGroup, totalWidth: number): void {
		const levelProducts: Array<ProductGroup> = this._productsToLevel.get(group.level) || [];
		group.level.addProducts(Utils.unique(levelProducts.map((pg: ProductGroup) => pg.product.id).concat(group.level.products)));
		group.level.actualMeters = Utils.round(levelProducts.reduce((total: number, product: ProductGroup) => total + product.Width, 0));
		group.level.actualPercent = group.level.actualMeters / totalWidth * 100;

		//this.products = [...this.products]; // for updating products and options in rule levels
	}

	public checkBlockProducts(products: Array<Product>, block: Block): boolean {
		for (let product of products) {
			if (block.isProductSuitable(product)) return true;
		}
		return false;
	}

	private disableBlockWarning(level: RuleLevel): void {
		level.blockWarning.BlockWarning = { value: false, block: null };
		if (level.children.length) level.children.forEach((child: RuleLevel) => this.disableBlockWarning(child));
	}

	public dividePlanogram(params: DivideParams, indexes: Array<string> = [], width: number = 0): void {
		params.groups.forEach((group: LayoutGroup) => {
			let groupIndexes: Array<string> = [...indexes], groupWidth: number = width;
			if (group.level.parent?.group.isHorizontal) {
				groupIndexes = group.level.id.split(', ');
				if (indexes.length) groupIndexes = groupIndexes.filter((index: string) => indexes.includes(index));
				group.width = Utils.round(groupWidth / 100 * group.level.parent.calculatedPercent);
			} else {
				group.width = Utils.round(groupWidth / 100 * group.level.calculatedPercent);
			}

			group.limits = {
				from: params.from,
				to: Math.min(Utils.round(params.from + group.width), params.to)
			};

			if (params.activeBlock.id && !params.activeBlock.shelves.length) {
				group.shelfLimits = [];
			} else {
				group.shelfLimits = params.planogram
					.getShelvesLimits(group.limits.from, group.limits.to, groupIndexes)
					.filter((limit: any) => {
						return limit.shelves.every((shelf: Element) => {
							const baseWithCentralPillar: boolean = shelf.relatedElements
								.some((e: Element) => e.parent.is(EquipmentElements.CentralPillar));

							return !shelf.depend?.parent.is(EquipmentElements.CentralPillar) && !baseWithCentralPillar;
						})
					});
			}

			group.level.plannedMeters = Utils.round(group.shelfLimits.reduce((total: number, limit: Limit) => total + limit.width, 0));
			group.shelfLimits = Utils.to1DArray(group.shelfLimits.map((limit: Limit) => {
				const blocks: Array<Block> = [],
					productsList: Array<Product> = group.level.parent?.group.isHorizontal ? params.products : group.products;

				limit.blocks.forEach((block: Block) => {
					if (!this.checkBlockProducts(productsList, block)) {
						blocks.push(block);
						this.enableBlockWarning(group.level, block);
						if (!group.level.children.length) {
							this.addBlockLimit(block, limit, group, productsList);
						}
					} else if (!block.hasEmptySettings && !group.level.children.length) {
						blocks.push(block);
						this.addBlockLimit(block, limit, group, productsList);
					}
				});
				if (blocks.length) return this.excludeBlocksFromLimit(blocks, limit);
				return limit;
			}));
			group.blocks = Utils.unique(Utils.to1DArray(group.shelfLimits.map((limit: Limit) => limit.blocks)))
				.filter((block: Block) => !block.hasEmptySettings);
			group.level.shelfIndexes = groupIndexes;

			if (group.level.parent?.group.isHorizontal) {
				const planogramLimits: Array<Limit> = params.planogram.getShelvesLimits(group.limits.from, group.limits.to),
					planogramMeters: number = planogramLimits.reduce((total: number, limit: Limit) => total + (limit.max - limit.min), 0);
				group.level.recommendedPercent = planogramMeters ? Utils.round(group.level.plannedMeters / planogramMeters * 100) : 0;
				group.level.finalPercent = group.level.recommendedPercent;
				group.limits = {
					from: Math.min(...group.shelfLimits.map((limit: Limit) => limit.min)),
					to: Math.max(...group.shelfLimits.map((limit: Limit) => limit.max))
				};
				group.width = Utils.round(group.limits.to - group.limits.from);
				groupWidth = group.width;
			}

			group.boxes = params.planogram.availableElements
				.filter((el: Element) => el.is(BoxElement) && el.shapes[0].inRange(group.limits) && !el.products.length)
				.sort(Utils.sortShelves()) as Array<BoxElement>;

			if (group.children.length) {
				this.dividePlanogram({
					groups: group.children,
					products: group.products,
					planogram: params.planogram,
					activeBlock: params.activeBlock,
					from: group.limits.from,
					to: params.to
				}, groupIndexes, groupWidth);
			} else {
				group.shelfLimits.forEach((limit: Limit) => {
					if (this._limitMatrix.hasOwnProperty(limit.altitude)) {
						this._limitMatrix[limit.altitude].push(limit);
					} else {
						this._limitMatrix[limit.altitude] = [limit];
					}
				});
			}

			if (group.level.parent && !group.level.parent.group.isHorizontal) {
				params.from = group.limits.to;
			}
		});
	}

	private enableBlockWarning(level: RuleLevel, block: Block): void {
		level.blockWarning.BlockWarning = {
			value: true,
			block: {
				name: block.name,
				begin: {
					x: block.begin[0].x,
					y: block.begin[0].y
				}
			}
		};
		if (level.parent) this.enableBlockWarning(level.parent, block);
	}

	private excludeBlocksFromLimit(blocks: Array<Block>, limit: Limit): Array<Limit> {
		const startPoints: Array<number> = blocks.map((block: Block) => block.boundingX.min).sort(),
			endPoints: Array<number> = blocks.map((block: Block) => block.boundingX.max).sort(),
			limits: Array<Limit> = [];

		startPoints.push(limit.max);
		endPoints.unshift(limit.min);

		if (startPoints[0] <= limit.min) {
			startPoints.shift();
			endPoints.shift();
		}
		if (endPoints[endPoints.length - 1] >= limit.max) {
			startPoints.pop();
			endPoints.pop();
		}

		for (let i = 0; i < endPoints.length; i++) {
			limits.push({
				altitude: limit.altitude,
				min: endPoints[i],
				strictMin: i == 0,
				max: startPoints[i],
				strictMax: i == endPoints.length - 1,
				shelves: limit.shelves.filter((shelf: Element) => shelf.shapes[0].inRange({
					from: endPoints[i],
					to: startPoints[i]
				})),
				blocks: limit.blocks.filter((block: Block) => block.inRange({
					from: endPoints[i],
					to: startPoints[i]
				})),
				get width() {
					return Utils.round(this.max - this.min);
				}
			});
		}

		return limits;
	}

	private expandFacesInGroup(group: LayoutGroup): void {
		if (this.expandFaces == ExpandFaces.Default) return;

		group.shelfLimits.forEach((limit: any) => {
			const blockPoints: Array<number> = this.getBlockPoints(limit);

			for (let i = 0; i < blockPoints.length - 1; i++) {
				const limitProducts: Array<ProductGroup> = Utils.to1DArray(limit.shelves
						.reduce((products: Array<ProductGroup>, shelf: Element) => [...products, ...shelf.products], [])
						.filter((product: ProductGroup) => product[0].begin.x >= blockPoints[i] && product[0].begin.x < blockPoints[i + 1])
					),
					limitProductsWidth: number = limitProducts.reduce(
						(width: number, product: ProductGroup) => Utils.round(width + product.Width), 0
					);

				const params: ExpandParams = {
					diff: Utils.round((blockPoints[i + 1] - blockPoints[i]) - limitProductsWidth),
					minWidth: Math.min(...limitProducts.map((product: ProductGroup) => product.ItemWidth)),
					levelToProduct: this._levelToProduct,
					productsToLevel: this._productsToLevel,
					percentToProduct: this._percentToProduct,
					cachedPercentDictionary: this.percentDictionary
				};
				ExpandFactory.getStrategy(this.expandFaces).executeInGroup(limitProducts, params);
			}
		});
	}

	public expandFacesOnPlanogram(limits: Array<any>, totalWidth: number): void {
		if (this.expandFaces == ExpandFaces.Default) return;

		limits.forEach((limit: any) => {
			const blockPoints: Array<number> = this.getBlockPoints(limit);

			for (let i = 0; i < blockPoints.length - 1; i++) {
				const limitProducts: Array<ProductGroup> = Utils.to1DArray(limit.shelves
						.reduce((products: Array<ProductGroup>, shelf: Element) => [...products, ...shelf.products], [])
						.filter((product: ProductGroup) => product[0].begin.x >= blockPoints[i] && product[0].begin.x < blockPoints[i + 1])
					),
					limitProductsWidth: number = limitProducts.reduce(
						(width: number, product: ProductGroup) => Utils.round(width + product.Width), 0
					);

				const params: ExpandParams = {
					diff: Utils.round((blockPoints[i + 1] - blockPoints[i]) - limitProductsWidth),
					minWidth: Math.min(...limitProducts.map((product: ProductGroup) => product.ItemWidth)),
					levelToProduct: this._levelToProduct,
					productsToLevel: this._productsToLevel,
					percentToProduct: this._percentToProduct,
					cachedPercentDictionary: this.percentDictionary,
					totalWidth: totalWidth
				};
				ExpandFactory.getStrategy(this.expandFaces).executeOnPlanogram(limitProducts, params);
			}
		});
	}

	private fillFreeSpace(facesGroups: Array<FacesProduct>, maxFacesQty: number, groupWidth: number): void {
		let facesWidth: number = Utils.round(
				facesGroups.reduce((a: number, b: FacesProduct) => a + b.qty * b.product.width, 0)
			),
			minWidth: number = Math.min(
				...facesGroups
					.filter((item: FacesProduct) => maxFacesQty ? item.qty < maxFacesQty : true)
					.map((item: FacesProduct) => item.product.width)
			),
			difference: number = Utils.round(groupWidth - facesWidth);

		if (difference >= minWidth) {
			let zeroFacesGroups: Array<FacesProduct> = facesGroups.filter((group: FacesProduct) => !group.qty);
			for (let product of zeroFacesGroups) {
				if (difference < minWidth) break;
				if (product.product.width > difference) continue;
				product.qty = 1;
				facesWidth = Utils.round(facesWidth + product.product.width);
				difference = Utils.round(groupWidth - facesWidth);
			}
			const sortedFacesGroup: Array<FacesProduct> = this.useSales
				? [...facesGroups]
				: [...facesGroups].sort((a: FacesProduct, b: FacesProduct) => a.product.width * a.qty - b.product.width * b.qty);
			for (let product of sortedFacesGroup) {
				if (difference < minWidth) break;
				if (product.product.width > difference) continue;
				if (maxFacesQty && product.qty >= maxFacesQty) continue;
				product.qty += 1;
				facesWidth = Utils.round(facesWidth + product.product.width);
				difference = Utils.round(groupWidth - facesWidth);
			}
			if (!(maxFacesQty && facesGroups.every((product: FacesProduct) => product.qty == maxFacesQty))) {
				this.fillFreeSpace(facesGroups, maxFacesQty, groupWidth);
			}
		}
	}

	private getAvailableLimitMax(limit: Limit): number {
		if (!limit.strictMax && this._limitMatrix.hasOwnProperty(limit.altitude)) {
			let index: number = this._limitMatrix[limit.altitude].findIndex((l: Limit) => l == limit);
			if (index !== -1) {
				while (index < this._limitMatrix[limit.altitude].length - 1) {
					if (this._limitMatrix[limit.altitude][index + 1].strictMax) {
						return this._limitMatrix[limit.altitude][index + 1].max;
					}
					index++;
				}
				return this._limitMatrix[limit.altitude][index].max;
			}
		} else {
			return limit.max;
		}
	};

	private getBlockPoints(limit: Limit): Array<number> {
		const blockPoints: Array<number> = Utils.unique(
			Utils.to1DArray(
				limit.blocks
					.filter((block: Block) => !block.hasEmptySettings)
					.map((block: Block) => [block.boundingX.min, block.boundingX.max])
			)
		)
			.filter((point: number) => point > limit.min && point < limit.max)
			.sort();
		blockPoints.unshift(limit.min);
		blockPoints.push(limit.max);
		return blockPoints;
	}

	public getLevelSettings(level: RuleLevel, settings: GroupSettings): GroupSettings {
		if (!level.parent) return;
		if (!level.parent.group.type) return;

		const result: GroupSettings = Object.assign({}, settings);
		result[level.parent.group.type] = level.id;

		return result;
	}

	private getPercentMultiplier(level: RuleLevel): number {
		const percentMultiplier: number = level.recommendedPercent
			? level.plannedPercent / level.recommendedPercent || 1
			: 1;

		if (level.parent) return this.getPercentMultiplier(level.parent) * percentMultiplier;

		return percentMultiplier;
	}

	public groupProducts(params: GroupParams): Array<LayoutGroup> {
		params.level.addProducts(params.products.map((product: Product) => product.id));

		this.disableBlockWarning(params.level);

		if (params.level.children.length) {
			const verticalSort: Array<SortBase> = params.sort.filter((sort: SortBase) => sort.isVertical);
			if (verticalSort.length && params.level.group.isHorizontal) {
				params.products = params.products.sort((a: Product, b: Product) => {
					if (verticalSort.length) {
						return verticalSort[0].sort(a, b, this.placePredict) * -1;
					}
					return 0;
				});
			}

			const ids: Array<string> = params.level.children.map((c: RuleLevel) => c.id),
				allGroups = params.level.group.group(params.products, params.level, params.planogram),
				groups = allGroups.filter(group => ids.includes(group.id)),
				result: Array<LayoutGroup> = [];

			this.setChildrenQueue(params.level, allGroups.filter(group => !ids.includes(group.id)));

			params.level.children.forEach((child: RuleLevel) => {
				const group = groups.find(g => g.id == child.id),
					groupPercent: number = params.percent / 100 * child.percent,
					groupSort: Array<SortBase> = [...params.sort, child.sort],
					groupSettings: GroupSettings = this.getLevelSettings(child, params.settings),
					products: Array<Product> = group ? group.products : [];

				child.addProducts(products.map((product: Product) => product.id));

				if (!child.children.length) {
					const queue = child.group.type ? child.group.group(products, child, params.planogram) : [];
					this.setChildrenQueue(child, queue);
				}

				if (!(
					params.level.group.isHorizontal &&
					params.activeBlock.shelves.length &&
					child.id.split(', ').every(id => !params.activeBlock.shelves.includes(id))
				)) {
					result.push({
						level: child,
						products,
						sort: groupSort,
						settings: groupSettings,
						children: child.children.length ?
							this.groupProducts({
								level: child,
								products: products,
								planogram: params.planogram,
								activeBlock: params.activeBlock,
								percent: groupPercent,
								settings: groupSettings,
								sort: groupSort
							}) : []
					});
				}
			});

			return params.level.parent ? result : [{
				level: params.level,
				products: params.products,
				sort: params.sort,
				children: result
			}];
		} else {
			const queue = params.level.group.type ? params.level.group.group(params.products, params.level, params.planogram) : [];
			this.setChildrenQueue(params.level, queue);
		}

		return [{
			level: params.level,
			products: params.products,
			sort: params.sort,
			children: []
		}];
	}

	private insertProduct(product: FacesProduct, params: InsertParams): void {
		if (!product.qty) return;
		if (!params.level && params.limits[params.index].level.maxFacesQty) {
			product.qty = Math.min(product.qty, params.limits[params.index].level.maxFacesQty);
		}

		let groupWidth: number = product.qty * product.product.width,
			element: Element = params.limits[params.index].shelves.find((shelf: Element) => {
				const bounding: Bounding = shelf.getBoundingX();
				return bounding.min <= params.from && params.from < bounding.max;
			});

		if (!element) {
			element = params.limits[params.index].shelves.find((shelf: Element) => {
				const bounding: Bounding = shelf.getBoundingX();
				return bounding.min - 0.01 <= params.from && params.from < bounding.max + 0.01;
			});
		}

		if (!element) return;

		let productGroup: ProductGroup = new ProductGroup({
			product: product.product,
			element: element,
			begin: { x: params.from, y: element.anchorYCoordinate, z: element.anchorZCoordinate },
			applied_compressions: (product as any).applied_compressions,
			detailedRotationPoint: this.productRotationPoint
		});

		if (
			productGroup.getBoundingX().max < params.limits[params.index].max &&
			this.checkDistance &&
			element.is(ShelfElement) &&
			(element as ShelfElement).hasShelfIntersections(productGroup)
		) return;

		let maxAvailable: number = this.getAvailableLimitMax(params.limits[params.index]);
		let maxForGroup: number = Math.min(maxAvailable, Utils.round(params.limits[params.index].max + params.extraSpace));
		while (params.index < params.limits.length - 1 && Utils.round(groupWidth + params.from) > params.limits[params.index].max) {
			let productGroupCopy: ProductGroup = productGroup.copy();
			element = params.limits[params.index].shelves.find((shelf: Element) => {
				const bounding: Bounding = shelf.getBoundingX();
				return bounding.min <= params.from && params.from < bounding.max;
			});

			// Якщо не знайшло елемент по limits (група вилазить за межі обладнання і т.д. і т.п.)
			if (!element) {
				element = params.limits[params.index].shelves[params.limits[params.index].shelves.length - 1];
			}

			productGroupCopy.Element = element;

			let freeSpace: number = Utils.round(maxForGroup - params.from),
				freeFacesQty: number = Math.floor(freeSpace / product.product.width),
				leftSpace: number = 0;

			if (freeFacesQty && freeFacesQty > 0) {
				productGroupCopy.begin.x = params.from;
				productGroupCopy.begin.y = element.getBoundingY().max;
				productGroupCopy.begin.z = element.getAbsoluteBoundingZ().max;

				if (
					this.checkDistance &&
					element.is(ShelfElement) &&
					(element as ShelfElement).hasShelfIntersections(productGroupCopy, params.from)
				) break;

				//if (element.products.some((group: Array<ProductGroup>) => group.some((p: ProductGroup) => p.product.id == productGroupCopy.product.id))) break;

				productGroupCopy.placementDepth = Math.floor(element.Depth / product.product.depth);
				productGroupCopy.FacesQty = freeFacesQty;

				if (!productGroupCopy.placementDepth || isNaN(productGroupCopy.placementDepth)) break;

				const layoutHeight = params.level ? params.level.layoutHeight : params.limits[params.index].level.layoutHeight;
				productGroupCopy.element.setProductPlacementHeight(productGroupCopy, layoutHeight);
				this.addProductToPlanogram(params.planogram, params.insertedProducts, productGroupCopy, params.level || params.limits[params.index].level);

				product.qty -= freeFacesQty;
				groupWidth = product.qty * product.product.width;

				leftSpace = Utils.round(params.limits[params.index].max - productGroupCopy.getBoundingX().max);
			} else {
				leftSpace = Utils.round(params.limits[params.index].max - params.from);
			}

			if (leftSpace != 0) {
				const shifted: boolean = this.shiftLimits(params.limits[params.index], leftSpace);
				if (shifted) params.extraSpace = Utils.round(params.extraSpace + leftSpace);
			}

			params.index++;
			params.from = params.limits[params.index].min;
			maxAvailable = this.getAvailableLimitMax(params.limits[params.index]);
			maxForGroup = Math.min(maxAvailable, Utils.round(params.limits[params.index].max + params.extraSpace));

			if (!params.level && params.limits[params.index].level.maxFacesQty) {
				product.qty = Math.min(product.qty, params.limits[params.index].level.maxFacesQty);
				groupWidth = product.qty * product.product.width;
			}
		}

		element = params.limits[params.index].shelves.find((shelf: Element) => {
			const bounding: Bounding = shelf.getBoundingX();
			return bounding.min <= params.from && params.from < bounding.max;
		});

		if (!element) {
			element = params.limits[params.index].shelves.find((shelf: Element) => {
				const bounding: Bounding = shelf.getBoundingX();
				return bounding.min - 0.01 <= params.from && params.from < bounding.max + 0.01;
			});
		}

		if (!element) return;

		productGroup.Element = element;

		//if (element.products.some((group: Array<ProductGroup>) => group.some(p => p.product.id == productGroup.product.id))) return;

		let freeFacesQty: number = product.qty;

		if (Utils.round(maxForGroup - params.from) < groupWidth) {
			freeFacesQty = Math.floor(Utils.round(maxForGroup - params.from) / product.product.width);
		}
		if (!freeFacesQty || freeFacesQty < 0) return;

		productGroup.begin.x = params.from;
		productGroup.begin.y = element.getBoundingY().max;
		productGroup.begin.z = element.getAbsoluteBoundingZ().max;

		if (
			this.checkDistance &&
			element.is(ShelfElement) &&
			(element as ShelfElement).hasShelfIntersections(productGroup, params.from)
		) return;

		productGroup.placementDepth = Math.floor(element.Depth / product.product.depth);
		productGroup.FacesQty = freeFacesQty;
		product.qty -= freeFacesQty;

		if (!productGroup.placementDepth || isNaN(productGroup.placementDepth)) return;

		const layoutHeight: number = params.level
			? params.level.layoutHeight
			: params.limits[params.index].level.layoutHeight;
		productGroup.element.setProductPlacementHeight(productGroup, layoutHeight);
		this.addProductToPlanogram(params.planogram, params.insertedProducts, productGroup, params.level || params.limits[params.index].level);

		params.from = Utils.round(params.from + productGroup.Width);

		if (params.from == params.limits[params.index].max && params.index < params.limits.length - 1) {
			params.index++;
			params.from = params.limits[params.index].min;
		}

		if (params.from > params.limits[params.index].max) {
			const leftSpace: number = Utils.round(params.limits[params.index].max - params.from);

			if (leftSpace != 0) {
				const shifted: boolean = this.shiftLimits(params.limits[params.index], leftSpace);
				if (shifted) params.extraSpace = Utils.round(params.extraSpace + leftSpace);
			}

			if (params.index < params.limits.length - 1) {
				params.index++;
				params.from = params.limits[params.index].min;
			}
		}
	}

	private insertProductGroups(group: LayoutGroup, planogram: EquipmentBase, totalWidth: number): void {
		const insertedProducts: Array<ProductGroup> = [];
		const facesGroups: Array<FacesProduct> = this.setFacesGroups(group, totalWidth);
		const groupWidth: number = group.shelfLimits
			.reduce((total: number, limit: Limit) => Utils.round(total + limit.width), 0);

		const params: InsertParams = {
			index: 0,
			from: group.shelfLimits[0].min,
			limits: group.shelfLimits,
			level: group.level,
			planogram: planogram,
			insertedProducts: insertedProducts,
			extraSpace: 0
		};

		facesGroups.forEach((product: FacesProduct) => this.insertProduct(product, params));

		group.shelfLimits.forEach((limit: Limit) => {
			if (limit.width < 0) {
				params.extraSpace = Utils.round(params.extraSpace + limit.width);
				this.shiftLimits(limit, limit.width);
			}
		})

		while (params.index < group.shelfLimits.length) {
			facesGroups.forEach((product: FacesProduct) => this.insertProduct(product, params));

			const currentWidth: number = group.shelfLimits
				.reduce((total: number, limit: Limit) => Utils.round(total + limit.width), 0);

			if (params.extraSpace != 0 || groupWidth != currentWidth) {
				const maxAvailable: number = this.getAvailableLimitMax(group.shelfLimits[params.index]);
				const availableSpace: number = Utils.round(maxAvailable - params.from);
				const leftSpace: number = Utils.round(group.shelfLimits[params.index].max - params.from);
				const shiftedSpace: number = leftSpace < -params.extraSpace ? leftSpace : Math.min(availableSpace, -params.extraSpace);

				if (shiftedSpace != 0) {
					const shifted: boolean = this.shiftLimits(group.shelfLimits[params.index], shiftedSpace);
					if (shifted) params.extraSpace = Utils.round(params.extraSpace + shiftedSpace);
				}
			}

			params.index++;
			if (params.index < group.shelfLimits.length) params.from = group.shelfLimits[params.index].min;
		}
	}

	public insertProducts(group: LayoutGroup, planogram: EquipmentBase, totalWidth: number): void {
		if (!group.shelfLimits || !group.shelfLimits.length) {
			group.level.actualMeters = 0;
			group.level.actualPercent = 0;
			return;
		}
		if (group.sort.some((sort: SortBase) => sort.key == 'shelf')) {
			group.shelfLimits.sort((a: Limit, b: Limit) => {
				const goldenA: boolean = GoldenShelf.isPointOn(a.shelves[0].shapes[0].begin.y),
					goldenB: boolean = GoldenShelf.isPointOn(b.shelves[0].shapes[0].begin.y);

				return goldenA && !goldenB ? -1 : !goldenA && goldenB ? 1 : 0;
			});
		}

		this.insertProductGroups(group, planogram, totalWidth);
		this.setHorizontalSort(group.sort, group.shelfLimits);
		this.expandFacesInGroup(group);
	}

	public insertProductsSequentially(productGroups: Array<LayoutGroup>, planogram: EquipmentBase, activeBlock: LayoutBlock): void {
		const shelfLimits: Array<any> = planogram.getShelvesLimits(activeBlock.start, activeBlock.end, activeBlock.shelves);
		const totalWidth: number = Utils.round(shelfLimits.reduce((a, b) => a + b.width, 0));
		const groupsToInsert: Array<LayoutGroup> = this.ungroupProducts(productGroups);
		this.insertProductsToBlock(planogram, totalWidth);

		let startX: number = activeBlock.start;
		let newStartX: number = activeBlock.start;
		const currentLimits: Array<any> = [];
		const insertedProducts: Array<ProductGroup> = [];

		groupsToInsert.forEach((group: LayoutGroup) => {
			group.level.plannedMeters = Utils.round(totalWidth * group.level.calculatedPercent / 100);
			const facesGroups: Array<FacesProduct> = this.setFacesGroups(group, totalWidth);
			facesGroups.forEach((product: FacesProduct) => {
				if (!product.qty) return;
				if (!currentLimits.length) {
					startX = newStartX;
					currentLimits.push(...this.getLimits(shelfLimits, startX));
				}
				const limit: any = currentLimits.shift();
				if (!limit) return;
				const correctX: number = group.level.fixedVerticalColumn
					? startX
					: this.findCorrectX(limit, startX, insertedProducts);
				if (product.product.width > Utils.round(limit.max - correctX)) {
					currentLimits.unshift(limit);
					return;
				}
				let element: Element = limit.shelves.find((shelf: Element) => {
					const bounding: Bounding = shelf.getBoundingX();
					return bounding.min <= correctX && correctX < bounding.max;
				});
				if (!element) {
					limit.shelves.find((shelf: Element) => {
						const bounding: Bounding = shelf.getBoundingX();
						return bounding.min <= startX && startX < bounding.max;
					});
				}
				if (!element) return;
				let productGroup: ProductGroup = new ProductGroup({
					product: product.product,
					element: element,
					begin: { x: correctX, y: element.anchorYCoordinate, z: element.anchorZCoordinate },
					applied_compressions: (product as any).applied_compressions,
					detailedRotationPoint: this.productRotationPoint
				});
				if (
					productGroup.getBoundingX().max < limit.max &&
					this.checkDistance &&
					element.is(ShelfElement) &&
					(element as ShelfElement).hasShelfIntersections(productGroup)
				) {
					currentLimits.unshift(limit);
					return;
				}
				productGroup.placementDepth = Math.floor(element.Depth / product.product.depth);
				productGroup.FacesQty = product.qty;
				if (productGroup.getBoundingX().max > limit.max) {
					productGroup.FacesQty = Math.floor((limit.max - productGroup.begin.x) / product.product.width);
				}
				productGroup.element.setProductPlacementHeight(productGroup, group.level.layoutHeight);
				newStartX = Math.max(newStartX, productGroup.getBoundingX().max);
				this.addProductToPlanogram(planogram, insertedProducts, productGroup, group.level);
			});
			this.calculateActualPercent(group, totalWidth);
		});

		this.expandFacesOnPlanogram(shelfLimits, totalWidth);

		groupsToInsert.forEach((group: LayoutGroup) => this.calculateActualPercent(group, totalWidth));
	}

	private getLimits(limits: Array<any>, point: number): Array<any> {
		return limits
			.filter((limit: any) => limit.min <= point && limit.max > point)
			.sort((a: any, b: any) => a.altitude - b.altitude);
	}

	private findCorrectX(limit: any, startX: number, inserted: Array<ProductGroup>): number {
		const products: Array<ProductGroup> = [];
		limit.shelves.forEach((shelf: ShelfElement) => {
			products.push(...shelf.sortedProducts.filter((pg: ProductGroup) => {
				return inserted.includes(pg) && pg.getBoundingX().min >= limit.min && pg.getBoundingX().max <= startX;
			}));
		});
		const maxProductX: number = Math.max(limit.min, ...products.map((pg: ProductGroup) => pg.getBoundingX().max));
		return Math.min(startX, maxProductX);
	}

	public insertProductsToBlock(planogram: EquipmentBase, totalWidth: number): void {
		const insertedProducts: Array<ProductGroup> = [];
		for (let item of this._blocksToLevel) {
			const verticalSort: Array<SortBase> = item[1].limits[0].sort.filter((sort: SortBase) => sort.isVertical);
			const blocksInLevel: Map<Block,Array<BlockLimit>> = new Map<Block,Array<BlockLimit>>();
			item[1].limits.forEach((limit: BlockLimit) => {
				if (blocksInLevel.has(limit.blocks[0])) {
					blocksInLevel.get(limit.blocks[0]).push(limit);
				} else {
					blocksInLevel.set(limit.blocks[0], [limit]);
				}
			});

			for (let block of blocksInLevel) {
				const products: Array<Product> = item[1].products
						.filter((product: Product) => this.checkBlockProducts([product], block[0])),
					totalBlockMeters: number = block[1].reduce((total: number, limit: any) => total + limit.width, 0),
					blockPercent: number = (totalBlockMeters * 100 / totalWidth) / 100,
					facesGroups: Array<FacesProduct> = this.setFacesGroupsForBlocks(products, blockPercent, totalBlockMeters, totalWidth, item[0].maxFacesQty)
						.sort((a: FacesProduct, b: FacesProduct) => {
							if (verticalSort.length) {
								return verticalSort[0].sort(a.product, b.product, this.placePredict);
							}
							return 0;
						});

				const params: InsertParams = {
					index: 0,
					from: block[1][0].min,
					limits: block[1],
					level: null,
					planogram: planogram,
					insertedProducts: insertedProducts,
					extraSpace: 0
				};

				facesGroups.forEach(product => this.insertProduct(product, params));

				while (params.index < block[1].length - 1) {
					params.index++;
					params.from = block[1][params.index].min;

					facesGroups.forEach(product => this.insertProduct(product, params));
				}

				block[1].forEach((limit: BlockLimit) => {
					this.setHorizontalSort(limit.sort, [limit]);
				});
			}
		}
		this._blocksInsertedProducts = Utils.unique(insertedProducts.map((pg: ProductGroup) => pg.product));
	}

	public recalculatePercents(group: LayoutGroup): void {
		let totalPercent: number = 0;
		let totalRecommendedPercent: number = Utils.round(
			group.level.children.reduce((total: number, child: RuleLevel) => total + child.recommendedPercent, 0),
			PERCENT_ACCURACY
		);

		group.children.forEach((child: LayoutGroup) => {
			if (totalRecommendedPercent < 100) {
				if (totalRecommendedPercent) {
					child.level.recommendedPercent = Utils.round(
						child.level.recommendedPercent * 100 / totalRecommendedPercent,
						PERCENT_ACCURACY
					)
				} else {
					child.level.recommendedPercent = Utils.round(
						100 / group.level.children.length,
						PERCENT_ACCURACY
					)
				}
			}
			const calculatedPercent: number = group.level.parent?.group.isHorizontal ? 100 : group.level.calculatedPercent;
			if (child.level.percent < 100 - totalPercent) {
				child.level.calculatedPercent = Utils.round(calculatedPercent / 100 * child.level.percent, PERCENT_ACCURACY);
				child.level.finalPercent = child.level.percent;
				totalPercent = Utils.round(totalPercent + child.level.percent, PERCENT_ACCURACY);
			} else {
				child.level.calculatedPercent = Utils.round(calculatedPercent / 100 * (100 - totalPercent), PERCENT_ACCURACY);
				child.level.finalPercent = Utils.round(100 - totalPercent, PERCENT_ACCURACY);
				totalPercent = 100;
			}
			this.recalculatePercents(child);
		});
	}

	private regroupProducts(groups: Array<LayoutGroup>, products: Array<Product>): void {
		if (!products.length || !groups.length) {
			groups.forEach((group: LayoutGroup) => group.products.length = 0);
			return;
		}

		if (products.length < groups.length) {
			const shelvesForProduct: number = groups.length / products.length;
			groups.forEach((group: LayoutGroup, idx: number) => {
				const pIdx: number = Math.floor(idx / shelvesForProduct);
				group.products = [products[pIdx]];
				group.level.addProducts(group.products.map((p: Product) => p.id));
			});
		} else {
			const calculatePercents = (groups: Array<LayoutGroup>) => {
				const shelvesPercent = new Map(),
					metersList = groups.map(group => (group.shelfLimits || []).reduce((total: number, limit: any) => total + limit.width, 0)),
					maxMeters = Utils.max(metersList);

				groups.forEach(group => shelvesPercent.set(group, Utils.round((group.shelfLimits || []).reduce((total: number, limit: any) => total + limit.width, 0) / (maxMeters || 1) * 100)));

				let percentSum: number = Array.from(shelvesPercent.values()).reduce((a, b) => a + b);

				return {
					productsPercent: products.length / (percentSum || 1),
					shelvesPercent: shelvesPercent
				};
			};

			let { productsPercent, shelvesPercent } = calculatePercents(groups);

			groups.forEach((group, index, array) => {
				const percent = shelvesPercent.get(group),
					groupSize = Utils.round(index === array.length - 1 ?
						products.length :
						productsPercent * percent, 0);
				group.products = products.splice(0, groupSize);
				group.level.addProducts(group.products.map(p => p.id));
			});
		}
	}

	private setChildrenQueue(level: RuleLevel, childrenQueue: Array<{ id: string, products: Array<Product> }>): void {
		level.childrenQueue.length = 0;
		Promise
			.all(childrenQueue.map(async (group: { id: string, products: Array<Product> }) => {
				let name: string = level.group.getChildrenName(group.id, this.names);
				if (level.group.isHorizontal) {
					this._translate('RULES.LEVEL.SHELF', { num: name }).subscribe((text: string) => name = text);
				}
				if (!name && level.group.type == 'brand') {
					this._translate('RULES.LEVEL.WITHOUT_BRAND').subscribe((text: string) => name = text);
				}
				if (!name && level.group.type == 'supplier') {
					this._translate('RULES.LEVEL.WITHOUT_SUPPLIER').subscribe((text: string) => name = text);
				}
				return { id: group.id, name };
			}))
			.then((result: Array<{ id: string, name: string }>) => level.childrenQueue.push(...result.sort(Utils.sortBy('name'))));
	}

	private setFacesGroups(group: LayoutGroup, totalWidth: number): Array<FacesProduct> {
		const verticalSort: Array<SortBase> = group.sort.filter((sort: SortBase) => sort.isVertical);
		const level: RuleLevel = group.level.parent?.group.isHorizontal ? group.level.parent : group.level;
		const blocksMeters: number = (this._blocksToLevel.get(level)?.limits || [])
				.filter((limit: BlockLimit) => limit.level == group.level)
				.reduce((total: number, limit: BlockLimit) => total + limit.width, 0);
		const blocksPercent: number = blocksMeters / group.level.plannedMeters;
		const percentMultiplier: number = this.getPercentMultiplier(group.level) * (1 - blocksPercent);
		const groupWidth: number = group.shelfLimits?.reduce((total: number, limit: BlockLimit) => Utils.round(total + limit.width), 0) || totalWidth;
		const facesGroups: Array<FacesProduct> = group.products
			.filter((product: Product) => {
				return !product.is_loose && groupWidth >= product.width && !this._blocksInsertedProducts.includes(product);
			})
			.map((product: Product, _: number, list: Array<Product>) => {
				const percent: number = this.useSales
						? this.percentDictionary[product.id]
						: group.level.planogramRecommendedPercent / list.length,
					availableWidth: number = totalWidth * percent * percentMultiplier,
					facesQty: number = Math.floor(availableWidth / product.width),
					maxFacesQty: number = group.level.maxFacesQty;
				return {
					qty: maxFacesQty && (facesQty > maxFacesQty) ? maxFacesQty : facesQty,
					product: product
				};
			})
			.sort((a: FacesProduct, b: FacesProduct) => {
				if (verticalSort.length) {
					return verticalSort[0].sort(a.product, b.product, this.placePredict);
				}
				return 0;
			});
		this.fillFreeSpace(facesGroups, group.level.maxFacesQty, group.level.plannedMeters - blocksMeters);
		return facesGroups;
	}

	private setFacesGroupsForBlocks(products: Array<Product>, blockPercent: number, blockMeters: number, totalMeters: number, maxFacesQty: number): Array<FacesProduct> {
		const totalProductsPercent: number = products
				.reduce((total: number, product: Product) => total + this.percentDictionary[product.id], 0),
			percentMultiplier: number = totalProductsPercent && blockPercent > totalProductsPercent
				? blockPercent / totalProductsPercent
				: 1,
			facesGroups: Array<FacesProduct> = products
				.map((product: Product, _: number, list: Product[]) => {
					const percent: number = this.useSales
							? this.percentDictionary[product.id] * percentMultiplier
							: blockPercent / list.length,
						availableWidth: number = totalMeters * percent,
						facesQty: number = Math.floor(availableWidth / product.width);
					return {
						qty: maxFacesQty && (facesQty > maxFacesQty) ? maxFacesQty : facesQty,
						product: product
					};
				});
		this.fillFreeSpace(facesGroups, maxFacesQty, blockMeters);
		return facesGroups;
	}

	private setHorizontalSort(sort: Array<SortBase>, limits: Array<Limit>): void {
		const horizontalSort: Array<SortBase> = sort
			.filter((sort: SortBase) => !sort.isVertical && sort.key && sort.key != 'shelf');
		if (horizontalSort.length) {
			limits.forEach((limit: Limit) => {
				const sortedGroups: Array<ProductGroup> = Utils.to1DArray(limit.shelves
					.reduce((groups: Array<Array<ProductGroup>>, shelf: Element) => [...groups, ...shelf.products], [])
					.filter((group: Array<ProductGroup>) => group[0].begin.x >= limit.min && group[0].begin.x < limit.max)
				);

				let products: Array<ProductGroup> = sortedGroups.sort((a: ProductGroup, b: ProductGroup) => {
					let index: number = 0,
						value: number = horizontalSort[index].sort(a.product, b.product);

					while (value == 0 && index < horizontalSort.length - 1) {
						index++;
						value = horizontalSort[index].sort(a.product, b.product);
					}

					return value;
				});

				products.reduce((a: number, b: ProductGroup) => {
					b.begin.x = a;
					return b.getBoundingX().max;
				}, limit.min);
			});
		}
	}

	public setRecommendedPercents(group: LayoutGroup): void {
		if (this.useSales) {
			const recommendedPercent: number = group.level.calculateRecommendedPercent(this.percentDictionary);
			group.children.forEach((child: LayoutGroup) => {
				if (recommendedPercent) {
					const percent: number = child.level.calculateRecommendedPercent(this.percentDictionary);
					child.level.recommendedPercent = Utils.round(percent / recommendedPercent * 100, PERCENT_ACCURACY);
				} else {
					child.level.recommendedPercent = 0
				}
				if (child.children.length) this.setRecommendedPercents(child);
			});
		} else {
			const totalWidth: number = group.products.reduce((sum: number, p: Product) => sum + p.width, 0);
			group.children.forEach((child: LayoutGroup) => {
				if (totalWidth) {
					const childWidth: number = child.products.reduce((sum: number, p: Product) => sum + p.width, 0);
					child.level.recommendedPercent = Utils.round(childWidth / totalWidth, PERCENT_ACCURACY);
				} else {
					child.level.recommendedPercent = 0;
				}
				if (child.children.length) this.setRecommendedPercents(child);
			});
		}
	}

	private shiftLimits(limit: Limit, delta: number): boolean {
		if (!limit.strictMax && this._limitMatrix.hasOwnProperty(limit.altitude)) {
			const limits: Array<Limit> = this._limitMatrix[limit.altitude];
			let index: number = limits.findIndex((l: Limit) => l == limit);
			if (index !== -1 && index < limits.length - 1) {
				limit.max = Utils.round(limit.max - delta);
				limits[index + 1].min = Utils.round(limits[index + 1].min - delta);
				limits[index + 1].shelves = Utils.unique([...limit.shelves, ...limits[index + 1].shelves]);
				return true;
			}
		}
		return false;
	};

	public ungroupProducts(groups: Array<LayoutGroup>): Array<LayoutGroup> {
		const result: Array<LayoutGroup> = [];

		groups.forEach((group: LayoutGroup) => {
			if (group.level.group.isHorizontal) {
				this.regroupProducts(
					group.children.filter((group: LayoutGroup) => !group.level.children.length),
					[...group.products.filter((product: Product) => !this._blocksInsertedProducts.includes(product))]
				);
			}
			if (group.children.length) {
				result.push(...this.ungroupProducts(group.children));
			} else {
				result.push(group);
			}
		});

		return result;
	}
}

@Injectable()
export class RulesService {

	cachedReportParams: any = {};
	cachedPlacePredict: any = {};
	cachedPercentDictionary: any = {};

	constructor(
		private sessionService: SessionService,
		private translate: TranslateService,
		private productsService: ProductsService
	) {}

	public async applyRule(params: ApplyRuleParams): Promise<EquipmentBase> {
		const translateCallback: (key: string, params: any) => Observable<any> = (key: string, params: any) => {
			return this.translate.get(key, params);
		};
		const ruleExecution: RuleExecution = new RuleExecution(translateCallback)
			.setCheckDistance(params.checkDistance)
			.setExpandFaces(params.expandFaces)
			.setNames(params.names)
			.setProductRotationPoint(this.sessionService.personalSettings.productRotationPoint)
			.setUseSales(params.useSales);

		const planogram: EquipmentBase = params.planogram.stand.copy();
		const ruleProducts: Array<Product> = [];
		if (params.activeBlock.id) {
			const block: Block = planogram.blocks.find((block: Block) => block.localId == params.activeBlock.id),
				dependedProducts: Array<ProductGroup> = [];

			block.products.forEach((pg: ProductGroup) => {
				dependedProducts.push(...Utils.findDependProducts(pg, planogram.products))
			});

			planogram.products = planogram.products
				.filter((pg: ProductGroup) => !block.products.includes(pg) && !dependedProducts.includes(pg));

			ruleProducts.push(...params.products.filter((product: Product) => ruleExecution.checkBlockProducts([product], block)))
		} else {
			planogram.products.length = 0;
			ruleProducts.push(...params.products);
		}

		const productGroups: Array<LayoutGroup> = ruleExecution.groupProducts({
				level: params.ruleLevels,
				products: ruleProducts,
				planogram: planogram,
				activeBlock: params.activeBlock,
				percent: params.ruleLevels.percent,
				settings: ruleExecution.getLevelSettings(params.ruleLevels, null),
				sort: [params.ruleLevels.sort]
			}),
			productIds: Array<number> = Utils.unique(
				Utils
					.to1DArray(ruleExecution.ungroupProducts(productGroups).map((group: LayoutGroup) => group.products))
					.map((product: Product) => product.id)
			),
			reportParams: any = Object.assign(
				{},
				params.reportParams,
				{ products: productIds.sort().join(','), shops: params.selectedStores.sort().join(',') },
				{ date_from: params.dateOptions.date_from, date_to: params.dateOptions.date_to }
			),
			percentDictionary: any = {};

		if (ruleExecution.useSales && !Utils.equalObjects(this.cachedReportParams, reportParams) && productIds.length) {
			const response = await this.productsService.getProductsPercent(
				Object.assign(
					{},
					params.reportParams || {},
					{ date_from: params.dateOptions.date_from, date_to: params.dateOptions.date_to }
				),
				productIds,
				params.planogram.planogram_stand[0]?.planogram,
				params.planogram.main_stand,
				params.selectedStores
			);

			for (let key in response) {
				if (response.hasOwnProperty(key)) percentDictionary[key] = (response[key].percent || 0) / 100;
			}

			this.cachedPercentDictionary = percentDictionary;
			this.cachedReportParams = reportParams;
			this.cachedPlacePredict = response;
		}

		ruleExecution.percentDictionary = this.cachedPercentDictionary;
		ruleExecution.reportParams = this.cachedReportParams;
		ruleExecution.placePredict = this.cachedPlacePredict;

		ruleExecution.setRecommendedPercents(productGroups[0]);
		ruleExecution.recalculatePercents(productGroups[0]);

		if (params.ruleLevels.direction == 'sequential') {
			ruleExecution.insertProductsSequentially(productGroups, planogram, params.activeBlock);
		} else {
			ruleExecution.dividePlanogram({
				groups: productGroups,
				products: ruleProducts,
				planogram: planogram,
				activeBlock: params.activeBlock,
				from: params.activeBlock.start,
				to: params.activeBlock.end
			}, params.activeBlock.shelves, params.activeBlock.width);

			const totalWidth: number = Utils.round(planogram.getShelvesLimits().reduce((a, b) => a + b.width, 0));
			ruleExecution.insertProductsToBlock(planogram, totalWidth);

			const groupsToInsert: Array<LayoutGroup> = ruleExecution.ungroupProducts(productGroups),
				groupsWidth: number = groupsToInsert.reduce((total: number, group: LayoutGroup) => {
					return total + group.shelfLimits.reduce((t: number, limit: Limit) => t + limit.width, 0);
				}, 0);

			groupsToInsert.forEach((group: LayoutGroup) => {
				ruleExecution.insertProducts(group, planogram, groupsWidth);
				ruleExecution.calculateActualPercent(group, totalWidth);
			});

			ruleExecution.expandFacesOnPlanogram(productGroups[0].shelfLimits, totalWidth);

			groupsToInsert.forEach((group: LayoutGroup) => ruleExecution.calculateActualPercent(group, totalWidth));
		}

		return planogram;
	}

	public getColorsSet(level: RuleLevel, colorsObj: object = {}): object {
		if (level.opened && level.children.length > 0) {
			level.children.forEach((child: RuleLevel) => {
				this.getColorsSet(child, colorsObj);
			});
		} else {
			level.products.reduce((colors: object, product: number) => {
				if (!colors[product]) colors[product] = {};
				if (!level.shelfIndexes.length) {
					colors[product].default = { color: level.color };
				} else {
					level.shelfIndexes.forEach((idx: string) => {
						colors[product][idx] = { color: level.color };
					});
				}
				return colors;
			}, colorsObj);
		}
		return colorsObj;
	}
}